import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Users, User, Mail, MoreVertical, ExternalLink, Phone, Trash2, ChevronUp, ChevronDown, Clock } from 'lucide-react';
import type { CustomerStatus, Temperature } from '../../types';
import type { ListItemView } from '../../types/list';
import { effectiveEmail, effectiveFirstName, effectiveLastName, effectivePhone } from '../../lib/personPrecedence';
import { formatPhone } from '../../lib/formatPhone';
import { CustomerStatusBadge } from '../organizations/CustomerStatusBadge';
import { TagChips } from '../tags/TagChips';
import { TEMPERATURE_STYLES } from '../shared/temperatureConfig';
import { ProgramItemCard } from './ProgramItemCard';
import { formatRelativeTime } from '../../lib/relativeTime';

interface ListItemCardProps {
  item: ListItemView;
  index: number;
  canEdit: boolean;
  onRemove: (item: ListItemView) => void;
  /** Reorder mode: swap rank circle for up/down chevrons */
  reorderMode?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

// Status → left-border color (copied from OrganizationCard's BORDER_COLOR map).
const ORG_BORDER: Record<CustomerStatus, string> = {
  PROSPECT: 'border-l-gray-400',
  ONBOARDED: 'border-l-blue-500',
  ACTIVE: 'border-l-green-500',
  SLIPPING: 'border-l-amber-500',
  LAPSED: 'border-l-red-500',
};

/**
 * Resolve the status-colored left border for a row, keyed by kind:
 * - SCHOOL/CLUB_TEAM → org.customerStatus
 * - PERSON → person.temperature (reuses TEMPERATURE_STYLES.borderL) with a neutral fallback
 * - PROGRAM → neutral gray
 */
function borderClass(item: ListItemView): string {
  if (item.kind === 'PERSON') {
    const temp = item.person?.temperature;
    return temp ? TEMPERATURE_STYLES[temp].borderL : 'border-l-gray-300 dark:border-l-gray-600';
  }
  if (item.kind === 'PROGRAM') {
    const orgStatus = item.team?.sport?.organization?.customerStatus;
    return orgStatus ? ORG_BORDER[orgStatus] : 'border-l-gray-400 dark:border-l-gray-500';
  }
  const status = item.organization?.customerStatus;
  return status ? ORG_BORDER[status] : 'border-l-gray-400';
}

// Small temperature pill for the PERSON badge slot (mirrors the mockup's "HOT" tag).
function TemperaturePill({ temperature }: { temperature: Temperature }) {
  const style = TEMPERATURE_STYLES[temperature];
  const { Icon } = style;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${style.selectedBg} ${style.selectedText}`}
    >
      <Icon className={`h-3 w-3 ${style.iconClass}`} />
      {style.label}
    </span>
  );
}

export function ListItemCard({
  item,
  index,
  canEdit,
  onRemove,
  reorderMode = false,
  isFirst = false,
  isLast = false,
  onMoveUp,
  onMoveDown,
}: ListItemCardProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Dependency-free dropdown: close on outside-click or Escape while open.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const org = item.organization;
  const person = item.person;
  const team = item.team;
  const unavailable = item.available === false;

  // Record route for "Open" (middle Link + menu action). PROGRAM resolves to its school.
  let recordHref: string | null = null;
  if ((item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM') && org) {
    recordHref = `/organizations/${org.id}`;
  } else if (item.kind === 'PERSON' && person) {
    recordHref = `/people/${person.id}`;
  } else if (item.kind === 'PROGRAM' && team?.sport?.organization) {
    recordHref = `/organizations/${team.sport.organization.id}`;
  }

  // Type icon for the row's name line.
  const TypeIcon =
    item.kind === 'SCHOOL' ? GraduationCap : item.kind === 'CLUB_TEAM' ? Users : User;

  // Name + subtitle (program renders its own middle via ProgramItemCard).
  let name = '';
  let subtitle = '';
  if (item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM') {
    name = org?.name ?? 'Unknown';
    subtitle = org ? [org.city, org.state].filter(Boolean).join(', ') : '';
  } else if (item.kind === 'PERSON') {
    name = person ? `${effectiveFirstName(person)} ${effectiveLastName(person)}`.trim() : 'Unknown';
    // "Position · School" — school from the primary active org link (CRM-114); either alone is fine.
    const school = person?.organizations?.[0]?.organization?.name;
    subtitle = [person?.position, school].filter(Boolean).join(' · ');
  }

  // Compact contact line for person rows (CRM-114) — text only: the card itself
  // links to the person, where tap-to-call lives; nested anchors would conflict.
  const contactPhone = item.kind === 'PERSON' && person ? effectivePhone(person) : null;
  const contactEmail = item.kind === 'PERSON' && person ? effectiveEmail(person) : null;

  // Org info line (CRM-115): assigned rep · coach count · enrollment (schools only).
  const isOrgRow = item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM';
  const orgInfoParts: string[] = [];
  if (isOrgRow && org) {
    if (org.coachCount !== undefined) orgInfoParts.push(`${org.coachCount} ${org.coachCount === 1 ? 'coach' : 'coaches'}`);
    if (item.kind === 'SCHOOL' && org.ncesEnrollment) orgInfoParts.push(`${org.ncesEnrollment.toLocaleString()} students`);
  }
  const orgRepName = isOrgRow ? org?.assignedRep?.displayName ?? null : null;
  const orgTags = isOrgRow ? org?.tags ?? [] : [];

  // Middle content (program uses ProgramItemCard; others use the icon + name/subtitle pair).
  const middle =
    item.kind === 'PROGRAM' ? (
      <ProgramItemCard team={team} />
    ) : (
      <>
        <div className="flex items-center gap-1.5 min-w-0">
          <TypeIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</span>
          {unavailable && (
            <span className="flex-shrink-0 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 text-[10px] font-semibold px-1.5 py-0.5">
              Unavailable
            </span>
          )}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
          {subtitle || '—'}
        </div>
        {(contactPhone || contactEmail) && (
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5 min-w-0">
            {contactPhone && (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                {formatPhone(contactPhone)}
              </span>
            )}
            {contactEmail && (
              <span className="inline-flex items-center gap-1 min-w-0 truncate">
                <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="truncate">{contactEmail}</span>
              </span>
            )}
          </div>
        )}
        {(orgRepName || orgInfoParts.length > 0) && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5 min-w-0 truncate">
            {orgRepName && (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                {orgRepName}
              </span>
            )}
            {orgRepName && orgInfoParts.length > 0 && <span>·</span>}
            {orgInfoParts.length > 0 && <span className="truncate">{orgInfoParts.join(' · ')}</span>}
          </div>
        )}
        {orgTags.length > 0 && (
          <div className="mt-1">
            <TagChips tags={orgTags} max={3} />
          </div>
        )}
      </>
    );

  const middleInner = <div className="flex-1 min-w-0 block">{middle}</div>;

  function handleOpen() {
    setMenuOpen(false);
    if (recordHref) navigate(recordHref);
  }

  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-sm flex items-center gap-2.5 border-l-4 ${borderClass(
        item,
      )} ${unavailable ? 'opacity-60' : ''}`}
    >
      {/* Left slot: rank circle in normal mode; up/down chevrons in reorder mode. */}
      {reorderMode ? (
        <div className="flex-none flex flex-col items-center gap-0">
          <button
            type="button"
            aria-label="Move up"
            disabled={isFirst}
            onClick={onMoveUp}
            className={`p-1 min-h-[40px] min-w-[40px] grid place-items-center rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors ${isFirst ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Move down"
            disabled={isLast}
            onClick={onMoveDown}
            className={`p-1 min-h-[40px] min-w-[40px] grid place-items-center rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors ${isLast ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex-none w-[22px] h-[22px] rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-bold grid place-items-center">
          {index + 1}
        </div>
      )}

      {/* Middle: link to the record in normal mode; plain div in reorder mode (no navigation). */}
      {reorderMode ? (
        middleInner
      ) : recordHref ? (
        <Link to={recordHref} className="flex-1 min-w-0 block">
          {middle}
        </Link>
      ) : (
        middleInner
      )}

      {/* Badge slot: status chip + last-activity line (right-aligned column). */}
      <div className="flex-none flex flex-col items-end gap-1">
        {(item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM') && org && (
          <CustomerStatusBadge status={org.customerStatus} size="sm" />
        )}
        {item.kind === 'PERSON' && person?.temperature && (
          <TemperaturePill temperature={person.temperature} />
        )}
        {item.kind === 'PROGRAM' && (
          <span className="inline-block rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-[10px] font-semibold px-2 py-0.5">
            PROGRAM
          </span>
        )}
        {/* Last-activity line — ORG and PERSON kinds only; PROGRAM has no concept. */}
        {(item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM') && (
          <span className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
            <Clock className="h-3 w-3 flex-shrink-0" />
            {formatRelativeTime(org?.lastActivityAt) ?? 'No activity'}
          </span>
        )}
        {item.kind === 'PERSON' && (
          <span className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
            <Clock className="h-3 w-3 flex-shrink-0" />
            {formatRelativeTime(person?.lastActivityAt) ?? 'No activity'}
            {person?.lastActivityAt && item.lastActivityBy ? ` · ${item.lastActivityBy}` : ''}
          </span>
        )}
      </div>

      {/* ⋮ menu — hidden in reorder mode; outside the Link so taps don't navigate. */}
      {!reorderMode && (
        <div className="flex-none relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Item actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="p-2 min-h-[40px] min-w-[40px] grid place-items-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-20"
            >
              {recordHref && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleOpen}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ExternalLink className="h-4 w-4 flex-shrink-0" />
                  Open record
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onRemove(item);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4 flex-shrink-0" />
                  Remove from list
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
